import React, {
	useState,
	useCallback,
	useMemo,
	Fragment,
	forwardRef,
	useRef
} from 'react'

import PropTypes from 'prop-types'
import styled, { css, keyframes } from 'styled-components'
import { twMerge } from 'tailwind-merge'

import { useOuterClickNotifier, useHotkey } from '../../../hooks'
import { Column, Row } from '../Grid'
import { Chevron, PaperPlane } from '../Icons/assets'
import { Text } from '../Text'

const Select = forwardRef(
	(
		{
			register,
			name,
			onChange,
			value,
			label,
			placeholder,
			options,
			error,
			disabled,
			icon,
			variant,
			clearButton,
			selectTextMarginTop = '12px',
			placeholderFontSize = 'small',
			chevronColor,
			title,
			className,
			...props
		},
		refRHF
	) => {
		const containerRef = useRef(null)
		useOuterClickNotifier(() => isOpen && setIsOpen(false), containerRef)

		const [isOpen, setIsOpen] = useState(false)
		const currentOption = useMemo(() => {
			const flattenedOptions = options?.reduce((prev, curr) => {
				if (curr.options) {
					return prev.concat(curr.options)
				}
				return prev.concat(curr)
			}, [])

			const currentValuePrimitive =
				value && typeof value === 'object' && value.value !== undefined
					? value.value
					: value

			if (
				currentValuePrimitive === undefined ||
				currentValuePrimitive === null ||
				currentValuePrimitive === ''
			) {
				return null
			}

			const searchString = String(currentValuePrimitive)

			return flattenedOptions?.find((op) => {
				const optionValue = op.value !== undefined ? op.value : op

				return String(optionValue) === searchString
			})
		}, [value, options])
		const selectedLabel = currentOption?.label || currentOption
		const hasOptionSelected = Boolean(currentOption)

		const toggleIsOpen = () => setIsOpen((previousIsOpen) => !previousIsOpen)
		useHotkey('Escape', () => isOpen && setIsOpen(false))

		const pickOption = useCallback(
			(op) => {
				setIsOpen(false)
				onChange({ target: { value: op.value || op, name } })
			},
			[name, onChange]
		)

		const renderOptions = (options, hasGroup = false) => {
			return options.map((option, index) => {
				if (option?.options) {
					return (
						<Fragment key={`${option.groupLabel}-${index}`}>
							<StyledOptionGroup as='li'>
								<Row>{option.groupLabel}</Row>
							</StyledOptionGroup>

							{renderOptions(option.options, true)}
						</Fragment>
					)
				}

				const optionValue = option.value !== undefined ? option.value : option
				const isSelected =
					String(optionValue) === String(currentOption?.value || currentOption)

				return (
					<StyledOption
						key={`${option.value || index}`}
						selected={isSelected}
						onClick={() => pickOption(option)}
						as='li'
						className={hasGroup ? 'ml-3' : ''}
					>
						<Row>
							{option.label || option}
							{icon && (
								<Row className='ml-[9px] hidden items-center'>
									<PaperPlane />
								</Row>
							)}
						</Row>
					</StyledOption>
				)
			})
		}

		const clearValue = () => {
			onChange({ target: { value: '', name } })
		}

		return (
			<Fragment>
				{variant === 'primary' && (
					<Container
						className={twMerge('w-full items-start', className)}
						{...props}
						ref={containerRef}
					>
						{title && (
							<Text className='mb-1 text-sm font-normal leading-5 text-[#2E3035]'>
								{title}
							</Text>
						)}

						<SelectBox
							data-testid='select-input-primary'
							variant={variant}
							type='button'
							disabled={disabled}
							onClick={toggleIsOpen}
							error={error}
							ref={refRHF}
						>
							{icon}
							<Row
								className={twMerge(
									'w-full items-center justify-between',
									icon && 'py-3 pl-3 pr-0',
									selectTextMarginTop === '12px' && 'mt-3'
								)}
								style={
									selectTextMarginTop !== '12px'
										? { marginTop: selectTextMarginTop }
										: undefined
								}
							>
								<Row>
									<Text
										className='absolute top-0.5 text-[10px] text-orange-600'
										as='label'
									>
										{label}
									</Text>
									<Text
										as='span'
										variant={hasOptionSelected ? 'tiny' : 'small'}
										className={twMerge(
											'absolute top-5',
											hasOptionSelected ? 'text-orange-600' : 'text-gray-800'
										)}
									>
										{selectedLabel}
									</Text>
									<Text
										as='span'
										variant={placeholderFontSize}
										className={twMerge(
											!hasOptionSelected ? 'inline' : 'hidden',
											'mt-1.5 text-gray-700'
										)}
									>
										{placeholder}
									</Text>
								</Row>
								{!disabled && (
									<Chevron
										size={24}
										color={
											chevronColor ? chevronColor : icon ? '#7F8C8D' : '#FF7708'
										}
										variant={!isOpen ? 'down' : 'top'}
									/>
								)}
							</Row>
						</SelectBox>
						{error && (
							<Text className='!mt-1.5 text-left text-[9px] text-red-700 sm:text-[11px]'>
								{error}
							</Text>
						)}
						{isOpen && (
							<OptionsContainer error={error}>
								<OptionList>
									{clearButton && (
										<ClearOption onClick={clearValue} as='li'>
											<Row>Limpar selecionado(a)</Row>
										</ClearOption>
									)}
									{renderOptions(options)}
								</OptionList>
							</OptionsContainer>
						)}
					</Container>
				)}
				{variant === 'secondary' && (
					<Container
						className={twMerge('w-full items-start', className)}
						{...props}
						ref={containerRef}
					>
						{title && (
							<Text className='mb-1 text-sm font-normal leading-5 text-[#2E3035]'>
								{title}
							</Text>
						)}

						<SelectBox
							data-testid='select-input-secondary'
							variant={variant}
							type='button'
							disabled={disabled}
							onClick={toggleIsOpen}
							error={error}
							ref={refRHF}
						>
							{icon}
							<Row
								className={twMerge(
									'w-full justify-between',
									icon && 'py-3 pl-3 pr-0'
								)}
							>
								<Row>
									{hasOptionSelected && variant !== 'primary' && (
										<Text
											className='absolute top-0 text-[10px] text-gray-700'
											as='label'
										>
											{variant === 'secondary' ? '' : label}
										</Text>
									)}

									<Text
										as='span'
										variant='small'
										className='absolute text-gray-800'
									>
										{selectedLabel}{' '}
									</Text>

									<Text
										as='span'
										className={twMerge(
											!hasOptionSelected ? 'inline' : 'hidden',
											'text-gray-700'
										)}
										variant='small'
									>
										{placeholder}
									</Text>
								</Row>
								{!disabled && (
									<Chevron
										size={20}
										color={
											chevronColor ? chevronColor : icon ? '#7F8C8D' : '#FF7708'
										}
										variant={!isOpen ? 'down' : 'top'}
									/>
								)}
							</Row>
						</SelectBox>
						{isOpen && (
							<OptionsContainer
								data-testid='select-secondary-primary'
								error={error}
							>
								<OptionList>
									{clearButton && (
										<ClearOption onClick={clearValue} as='li'>
											<Row>Limpar selecionado(a)</Row>
										</ClearOption>
									)}
									{renderOptions(options)}
								</OptionList>
							</OptionsContainer>
						)}
					</Container>
				)}
				{variant === 'borderless' && (
					<Container
						className={twMerge('w-full items-start', className)}
						ref={containerRef}
						{...props}
					>
						{title && (
							<Text className='mb-1 text-sm font-normal leading-5 text-[#2E3035]'>
								{title}
							</Text>
						)}

						<SelectBox
							data-testid='select-input-borderless'
							variant={variant}
							type='button'
							disabled={disabled}
							onClick={toggleIsOpen}
							error={error}
							ref={refRHF}
						>
							{icon}
							<Row className='w-full items-center justify-between'>
								<Text as='span' variant='regular'>
									{selectedLabel}{' '}
								</Text>

								<Text
									as='span'
									className={twMerge(
										!hasOptionSelected ? 'inline' : 'hidden',
										'text-gray-700'
									)}
									variant='small'
								>
									{placeholder}
								</Text>

								{!disabled && (
									<Chevron
										size={20}
										color={
											chevronColor ? chevronColor : icon ? '#7F8C8D' : '#FF7708'
										}
										variant={!isOpen ? 'down' : 'top'}
									/>
								)}
							</Row>
						</SelectBox>
						{isOpen && (
							<OptionsContainer
								data-testid='select-value-borderless'
								error={error}
							>
								<OptionList>
									{clearButton && (
										<ClearOption onClick={clearValue} as='li'>
											<Row>Limpar selecionado(a)</Row>
										</ClearOption>
									)}
									{renderOptions(options)}
								</OptionList>
							</OptionsContainer>
						)}
					</Container>
				)}
				{variant === 'tertiary' && (
					<Container
						className={twMerge('w-full items-start', className)}
						{...props}
						ref={containerRef}
					>
						{title && (
							<Text className='mb-1 text-sm font-normal leading-5 text-[#2E3035]'>
								{title}
							</Text>
						)}

						<SelectBox
							data-testid='select-input-tertiary'
							type='button'
							disabled={disabled}
							onClick={toggleIsOpen}
							error={error}
							ref={refRHF}
						>
							{icon}
							<Row
								className={twMerge(
									'mt-3 w-full justify-between',
									icon && 'py-3 pl-3 pr-0'
								)}
							>
								<Row>
									{hasOptionSelected && (
										<Text
											className='absolute top-0.5 text-[10px] text-orange-600'
											as='label'
										>
											{label}
										</Text>
									)}
									<Text
										as='span'
										variant='small'
										className='absolute top-5 text-gray-800'
									>
										{selectedLabel}{' '}
									</Text>
									<Text
										as='span'
										className={twMerge(
											!hasOptionSelected ? 'inline' : 'hidden',
											'text-gray-800'
										)}
										variant='regular'
									>
										{placeholder}
									</Text>
								</Row>
								{!disabled && (
									<Chevron
										size={20}
										color={
											chevronColor ? chevronColor : icon ? '#7F8C8D' : '#FF7708'
										}
										variant={!isOpen ? 'down' : 'top'}
									/>
								)}
							</Row>
						</SelectBox>
						{isOpen && (
							<OptionsContainer
								data-testid='select-value-tertiary'
								error={error}
							>
								<OptionList>
									{clearButton && (
										<ClearOption onClick={clearValue} as='li'>
											<Row>Limpar selecionado(a)</Row>
										</ClearOption>
									)}
									{renderOptions(options)}
								</OptionList>
							</OptionsContainer>
						)}
					</Container>
				)}
			</Fragment>
		)
	}
)

Select.displayName = 'Select'

const SelectBox = styled.button(
	({ error, disabled, variant, theme: { colors } }) => css`
		background-color: ${variant === 'secondary' ? '#F7F9FB' : colors.white};
		text-align: left;
		display: flex;
		align-items: center;
		padding-left: 12px;
		width: 100%;
		height: 48px;
		position: relative;
		cursor: ${!disabled ? 'pointer' : 'not-allowed'};
		transition: all 0.3s ease;
		font-style: normal;
		font-weight: normal;
		font-size: 14px;
		line-height: 24px;
		overflow: hidden;

		${variant === 'borderless'
			? css`
					border: none;
					color: ${colors.primary};
					font-weight: bold;
					&:focus {
						outline: none;
						border: none;
					}
				`
			: css`
					color: ${disabled ? colors.gray.n300 : '#2E3035'};
					border-radius: 4px;
					border: 0.5px solid #e7eaf4;

					&:focus {
						outline: none;
						border: 0.5px solid black;
					}
				`}
	`
)

const OptionsContainer = styled.div(
	({ error, theme: { colors } }) => css`
		z-index: 25;
		max-height: 259px;
		position: absolute;
		width: 100%;
		background: ${colors.white};
		border: 1px solid #e7eaf4;
		border-radius: 4px;
		overflow: auto;
		top: ${error ? 'calc(100% - 18px)' : '52px'};

		::-webkit-scrollbar {
			width: 3px;
			background-color: ${colors.gray.n200};
		}
		::-webkit-scrollbar-thumb {
			background-color: #babac0;
			border-radius: 16px;
		}
		::-webkit-scrollbar-button {
			display: none;
		}
	`
)

const isOpenMenu = keyframes`
  from {
    max-height: 0px;
  }
  to {
    max-height: 200px;
  }
`

const OptionList = styled.ul`
	animation: ${isOpenMenu} 0.3s ease;
	background-color: #fff;
	margin: 0;
	padding: 0;
	overflow-x: hidden;
`

const Container = styled(Column)`
	position: relative;
`

const StyledOption = styled(Text)(
	({ theme: { colors } }) => css`
		text-align: left;
		list-style-type: none;
		padding: 8px 16px;
		cursor: pointer;
		font-size: 14px;
		&:hover {
			color: ${colors.primary};
		}
		&:focus {
			outline: none;
		}
	`
)

const ClearOption = styled(StyledOption)(
	({ theme: { colors } }) => css`
		border-bottom: 1px solid ${colors.gray.n300};
	`
)

const StyledOptionGroup = styled(Text)(
	({ theme: { colors } }) => css`
		text-align: left;
		list-style-type: none;
		padding: 8px 16px;
		cursor: default;
		font-size: 14px;
		line-height: 24px;
		color: ${colors.gray.n750};
	`
)

Select.defaultProps = {
	option: [],
	disabled: false,
	variant: 'primary',
	clearButton: false
}

Select.propTypes = {
	label: PropTypes.string,
	options: PropTypes.arrayOf(
		PropTypes.oneOfType([
			PropTypes.shape({
				value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
					.isRequired,
				label: PropTypes.string.isRequired
			}),
			PropTypes.shape({
				groupLabel: PropTypes.string.isRequired,
				options: PropTypes.arrayOf(
					PropTypes.shape({
						value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
							.isRequired,
						label: PropTypes.string.isRequired
					})
				)
			})
		])
	).isRequired,
	register: PropTypes.func,
	error: PropTypes.string,
	name: PropTypes.string,
	value: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
		PropTypes.object
	]),
	disabled: PropTypes.bool,
	variant: PropTypes.string,
	clearButton: PropTypes.bool,
	className: PropTypes.string
}

export { Select }
